import React, { useEffect, useRef, useState } from "react";

import { Link, useHistory } from "react-router-dom";

import type { RowTone } from "manager/components/vero1/Vero1ReviewPanel/formatItemValue";
import { openDealReview } from "manager/components/vero1/openDealReview";
import { Vero1CardVerdict } from "manager/components/vero1/vero1BannerState";
import { ResetIdFraudRiskModal } from "manager/components/app-summary/modals/reset-idfraudrisk-modal/component";
import { VerifyIdFraudRiskModal } from "manager/components/app-summary/modals/verify-idfraudrisk-modal/component";
import {
  INTERNAL_USER_RESET_DISABLED_REASON,
  INTERNAL_USER_VERIFY_DISABLED_REASON,
} from "manager/components/app-summary/idFraudRiskStaffCopy";
import { ShieldCheckIcon } from "manager/components/app-summary/modals/verify-idfraudrisk-modal/ShieldCheckIcon";
import { StrokeCheckIcon } from "manager/components/app-summary/modals/verify-idfraudrisk-modal/StrokeCheckIcon";
import {
  APPLICATION_STATUSES,
  colors,
  DEAL_UI_STATUS_CODES,
} from "shared/config/constants";
import Tooltip from "shared/components/Tooltip";
import { SENTILINK_IDENTITY_DISPLAY_NAME } from "shared/components/vega/ApplicantSummaryV2/sentilinkIdentityLines";
import { LockIcon, RedoIcon } from "shared/components/vega/icons";
import { usePermissions } from "shared/hooks";
import { useActiveUser } from "shared/hooks/api";
import useModal from "shared/hooks/useModal";
import { isCanceledDeal, isDecisionedDeal } from "shared/utils/deal-status";
import { buildClassName } from "shared/utils/vega";

import StatusIcon, { LgStatusIcon, StatusType, getStatusColor } from "../../StatusIcon";
import { useDealSummaryRedesign } from "../../../useDealSummaryRedesign";

import ApplicantDetailModal, { ModalSection } from "./ApplicantDetailModal";
import { Vero1UpdatedNote } from "./Vero1UpdatedNote";

export interface CardDetailItem {
  label?: string;
  value: string;
  status?: string;
  id?: string;
}

/** Persona (Government ID & Selfie) status shown beneath the fraud-risk block. */
export interface GovernmentIdSelfieData {
  verified: boolean;
  label: string | null;
}

/** PRODUCT-2237: side-by-side name mismatch on the ID Verification card. */
export interface SentilinkNameMismatchCardData {
  idCredentialName: string;
  submittedName: string;
  anchorSource?: string | null;
}

/**
 * Structured Identity Risk Check data for the ID Verification card
 * (PRODUCT-1346): tier, blocked/error state, reason descriptions, Persona status,
 * and the reset affordance metadata.
 */
export interface IdentityFraudRiskCardData {
  applicationId: string | number;
  providerSentilinkId?: number | null;
  triggered: boolean;
  isBlocked: boolean;
  isError: boolean;
  isNeedsReview: boolean;
  isFailedVerifiable?: boolean;
  isManuallyVerified: boolean;
  tierLabel: string | null;
  originalTierLabel?: string | null;
  tierStatus: StatusType | null;
  reasons: string[];
  awaitingResubmission: boolean;
  resetCount: number;
  resetLimit: number | null;
  applicantEmail?: string;
  checkedAt?: string | null;
  manualVerification?: {
    reviewerName?: string | null;
    reviewerEmail?: string | null;
    reviewedItemLabel?: string | null;
    details?: string | null;
    verifiedAt?: string | null;
  } | null;
  governmentId: GovernmentIdSelfieData;
  nameMismatch?: SentilinkNameMismatchCardData;
}

export interface ApplicantGroupCardProps {
  cardTitle: string;
  status: StatusType;
  cardDetails: CardDetailItem[];
  cardModalDetails?: ModalSection[];
  allDetailsLink?: string;
  cosignApproved?: boolean;
  cosignUuid?: string;
  identityFraudRisk?: IdentityFraudRiskCardData;
  applicantName?: string;
  applicantRole?: string;
  applicationStatus?: APPLICATION_STATUSES;
  dealStatus?: DEAL_UI_STATUS_CODES;
  autoOpenModal?: boolean;
  neutral?: boolean;
  vero1Verdict?: Vero1CardVerdict;
  dealId?: string | number;
  vero1ReviewCategory?: string;
  vero1Edited?: boolean;
  vero1CardTone?: RowTone;
}

export type ApplicantCardDetailsProps = ApplicantGroupCardProps;

// PRODUCT-2831: Vero staff keep Reset/Verify on the card, disabled with the copy
// above, instead of hidden. Deliberate exception to disable-not-hide; do not revert.
export {
  INTERNAL_USER_RESET_DISABLED_REASON,
  INTERNAL_USER_VERIFY_DISABLED_REASON,
};

const formatManualVerificationDate = (verifiedAt?: string | null) => {
  if (!verifiedAt) {
    return null;
  }
  const parsed = new Date(verifiedAt);
  if (Number.isNaN(parsed.getTime())) {
    return null;
  }
  return parsed.toLocaleString("en-US", {
    year: "numeric",
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

/**
 * In-card Identity Risk Check block (PRODUCT-1346): BLOCKED pill, tier
 * label, reason chip, Government ID & Selfie line, and the inline Reset action.
 */
const IdentityFraudRiskBlock: React.FC<{
  data: IdentityFraudRiskCardData;
  canShowResetAction: boolean;
  canShowVerifyAction: boolean;
  atResetLimit: boolean;
  resetDisabled: boolean;
  resetDisabledReason?: string | null;
  verifyDisabled: boolean;
  verifyDisabledReason?: string | null;
  terminalTierLabel?: string | null;
  onReset: (e?: React.MouseEvent) => void;
  onVerify: (e?: React.MouseEvent) => void;
  neutral?: boolean;
}> = ({
  data,
  canShowResetAction,
  canShowVerifyAction,
  atResetLimit,
  resetDisabled,
  resetDisabledReason,
  verifyDisabled,
  verifyDisabledReason,
  terminalTierLabel,
  onReset,
  onVerify,
  neutral,
}) => {
  const lgTone = useDealSummaryRedesign();
  const {
    isBlocked,
    isError,
    isManuallyVerified,
    tierLabel,
    tierStatus,
    reasons,
    awaitingResubmission,
    resetLimit,
    manualVerification,
    governmentId,
    nameMismatch,
  } = data;

  const reasonCount = reasons.length;
  const verifiedAtDisplay = formatManualVerificationDate(
    manualVerification?.verifiedAt
  );
  const reviewerDisplay =
    manualVerification?.reviewerName || manualVerification?.reviewerEmail;

  // Blocked → error-600; Needs Review / Error → dark warning-700 (not orange).
  const tierColor = neutral
    ? undefined
    : isBlocked
      ? colors.error[600]
      : tierStatus === "warn"
        ? colors.warning[700]
        : tierStatus && getStatusColor(tierStatus);

  return (
    <div
      className="app-summary-v2 idfraudrisk-block"
      data-testid="idfraudrisk-block"
    >
      <div
        className={buildClassName([
          "idfraudrisk-box",
          // Only a blocked check gets the red treatment; an errored check is a
          // soft warning (dark warning-700) per PRODUCT-1282, like Needs Review.
          isBlocked && "critical",
          (data.isNeedsReview || isError) && !isManuallyVerified && "warn",
          isManuallyVerified && "verified",
        ])}
        data-testid="idfraudrisk-box"
      >
        <div className="idfraudrisk-heading">
          <span className="idfraudrisk-title">{SENTILINK_IDENTITY_DISPLAY_NAME}</span>
          {isBlocked && (
            <span
              className="idfraudrisk-pill blocked"
              data-testid="idfraudrisk-blocked-pill"
            >
              <LockIcon className="idfraudrisk-pill-icon" aria-hidden="true" />
              BLOCKED
            </span>
          )}
          {isManuallyVerified && (
            <span
              className="idfraudrisk-pill verified"
              data-testid="idfraudrisk-manual-verified-pill"
            >
              <StrokeCheckIcon size={9} className="idfraudrisk-pill-icon" />
              Manually verified
            </span>
          )}
        </div>

        {!isManuallyVerified && tierLabel && (
          <span
            className="idfraudrisk-tier"
            data-testid="idfraudrisk-tier"
            style={tierColor ? { color: tierColor } : undefined}
          >
            {terminalTierLabel ?? tierLabel}
          </span>
        )}

        {nameMismatch && (
          <div
            className="idfraudrisk-name-mismatch"
            data-testid="idfraudrisk-name-mismatch"
          >
            <div className="idfraudrisk-name-mismatch-label">
              Identity name mismatch
            </div>
            <div className="idfraudrisk-name-mismatch-grid">
              <div className="idfraudrisk-name-mismatch-col">
                <span className="idfraudrisk-name-mismatch-heading">
                  ID credential
                </span>
                <span className="idfraudrisk-name-mismatch-value">
                  {nameMismatch.idCredentialName}
                </span>
              </div>
              <div className="idfraudrisk-name-mismatch-col">
                <span className="idfraudrisk-name-mismatch-heading">
                  Submitted name
                </span>
                <span className="idfraudrisk-name-mismatch-value">
                  {nameMismatch.submittedName}
                </span>
              </div>
            </div>
          </div>
        )}

        {!isManuallyVerified && reasonCount > 0 && (
          <Tooltip
            placement="top"
            title={
              <ul className="idfraudrisk-reason-list">
                {reasons.map((reason, idx) => (
                  <li key={`idfraudrisk-reason-${idx}`}>{reason}</li>
                ))}
              </ul>
            }
          >
            <span
              className="idfraudrisk-reason-chip"
              data-testid="idfraudrisk-reason-chip"
              onClick={(e) => e.stopPropagation()}
            >
              {reasonCount} reason{reasonCount === 1 ? "" : "s"} &middot; hover
              to view
            </span>
          </Tooltip>
        )}

        {(canShowResetAction || canShowVerifyAction) && (
          // A click on a disabled action must not fall through to the card's
          // detail modal (PRODUCT-2831); enabled buttons stop it themselves.
          <div
            className="idfraudrisk-actions"
            role="presentation"
            onClick={(e) => e.stopPropagation()}
          >
            {canShowResetAction && (
              <>
                <button
                  type="button"
                  data-testid="reset-idfraudrisk-button"
                  onClick={onReset}
                  disabled={atResetLimit || resetDisabled}
                  aria-describedby={
                    resetDisabledReason
                      ? `reset-idfraudrisk-disabled-${data.applicationId}`
                      : undefined
                  }
                  className="idfraudrisk-reset-button"
                >
                  <RedoIcon className="idfraudrisk-reset-icon" aria-hidden="true" />
                  Reset Identity Risk
                </button>
                {resetDisabledReason ? (
                  <div
                    id={`reset-idfraudrisk-disabled-${data.applicationId}`}
                    className="idfraudrisk-reset-disabled-reason"
                    data-testid="reset-idfraudrisk-disabled-reason"
                  >
                    {resetDisabledReason}
                  </div>
                ) : (
                  atResetLimit && (
                    <div
                      className="idfraudrisk-reset-limit"
                      data-testid="reset-idfraudrisk-limit"
                    >
                      Reset limit reached ({resetLimit}).
                    </div>
                  )
                )}
              </>
            )}
            {canShowVerifyAction && (
              <>
                <button
                  type="button"
                  data-testid="verify-idfraudrisk-button"
                  onClick={onVerify}
                  disabled={verifyDisabled}
                  aria-describedby={
                    verifyDisabledReason
                      ? `verify-idfraudrisk-disabled-${data.applicationId}`
                      : undefined
                  }
                  className="card-verify-btn"
                >
                  <ShieldCheckIcon size={12} className="idfraudrisk-reset-icon" />
                  Manually review identity
                </button>
                {verifyDisabledReason && (
                  <div
                    id={`verify-idfraudrisk-disabled-${data.applicationId}`}
                    className="idfraudrisk-verify-disabled-reason"
                    data-testid="verify-idfraudrisk-disabled-reason"
                  >
                    {verifyDisabledReason}
                  </div>
                )}
              </>
            )}
          </div>
        )}

        {isManuallyVerified && (
          <>
            {(reviewerDisplay || verifiedAtDisplay) && (
              <div
                className="idfraudrisk-verified-meta"
                data-testid="idfraudrisk-manual-verified-details"
              >
                {reviewerDisplay ? `by ${reviewerDisplay}` : "Manually verified"}
                {verifiedAtDisplay ? ` · ${verifiedAtDisplay}` : ""}
              </div>
            )}
            <div
              className="idfraudrisk-retained"
              data-testid="idfraudrisk-manual-verified-retained"
            >
              Original: {data.originalTierLabel ?? "Needs Review"} · retained
              for audit
            </div>
          </>
        )}

        {awaitingResubmission && (
          <div
            className="idfraudrisk-awaiting"
            data-testid="idfraudrisk-awaiting-resubmission"
          >
            Awaiting re-submission
          </div>
        )}
      </div>

      <div className="idfraudrisk-persona">
        {governmentId.verified ? (
          <span
            className="idfraudrisk-govid verified"
            data-testid="idfraudrisk-govid"
          >
            {!neutral &&
              (lgTone ? <LgStatusIcon status="pass" /> : <StatusIcon status="pass" />)}
            {governmentId.label} &middot; Verified
          </span>
        ) : (
          <span className="idfraudrisk-govid" data-testid="idfraudrisk-govid">
            Government ID &amp; Selfie:{" "}
            <strong className="idfraudrisk-govid-value">Not started</strong>
          </span>
        )}
        {!governmentId.verified && (
          <span className="idfraudrisk-persona-note">
            Runs after the identity risk check
          </span>
        )}
      </div>
    </div>
  );
};

/**
 * Component that displays a card with status icon and details
 */
const ApplicantGroupCard: React.FC<ApplicantGroupCardProps> = ({
  cardTitle,
  status,
  cardDetails,
  cardModalDetails,
  allDetailsLink,
  cosignApproved = false,
  cosignUuid,
  identityFraudRisk,
  applicantName,
  applicantRole,
  applicationStatus,
  dealStatus,
  autoOpenModal = false,
  neutral = false,
  vero1Verdict,
  dealId,
  vero1ReviewCategory,
  vero1Edited = false,
  vero1CardTone,
}) => {
  const lgTone = useDealSummaryRedesign();
  const history = useHistory();
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [isResetModalOpen, setIsResetModalOpen] = useState(false);
  const [isVerifyModalOpen, setIsVerifyModalOpen] = useState(false);
  // Synchronous mirror of isResetModalOpen: state updates are async, so the ref
  // is what actually blocks a rapid second click from stacking reset dialogs.
  const resetModalOpenRef = useRef(false);
  const verifyModalOpenRef = useRef(false);
  const didAutoOpenModal = useRef(false);
  const {
    canResetIdFraudRisk,
    canVerifyIdFraudRisk,
    canVerifyFailedIdFraudRisk,
    isIdFraudRiskEnabled,
  } = usePermissions();
  const { activeUser } = useActiveUser();
  const isInternalUser = Boolean(activeUser?.isInternal);
  const { openModalDialog } = useModal();

  const openModal = () => setIsModalOpen(true);
  const closeModal = () => setIsModalOpen(false);

  useEffect(() => {
    if (autoOpenModal && cardModalDetails && !didAutoOpenModal.current) {
      didAutoOpenModal.current = true;
      setIsModalOpen(true);
    }
  }, [autoOpenModal, cardModalDetails]);

  const handleLinkClick = (e) => {
    e.stopPropagation(); // Prevent the card's onClick from triggering
  };

  const vero1Governs =
    vero1Verdict?.mode === "override" || vero1Verdict?.mode === "adopt";
  const showVero1Marker = Boolean(vero1Governs && vero1Edited);
  const showReviewChangesLink = showVero1Marker && dealId != null;

  const handleReviewChangesClick = (e: React.MouseEvent) => {
    e.stopPropagation();
    openDealReview(history, dealId as string | number, vero1ReviewCategory);
  };

  const atResetLimit =
    !!identityFraudRisk &&
    identityFraudRisk.resetLimit != null &&
    identityFraudRisk.resetCount >= identityFraudRisk.resetLimit;
  // Reset is only offered for a BLOCKED check: the backend reset endpoint
  // (PRODUCT-1346) rejects non-blocked records with 409 `not_blocked`, so an
  // errored check is surfaced as a critical state but is not resettable here.
  // PRODUCT-2831: the backend returns can_* false for Vero staff. Keep the
  // control on the card for them (disabled) where a manager would see it.
  const internalResetBlocked = isInternalUser && !canResetIdFraudRisk;
  const internalVerifyBlocked = isInternalUser && !canVerifyIdFraudRisk;
  const canShowResetAction =
    !!identityFraudRisk &&
    identityFraudRisk.isBlocked &&
    (canResetIdFraudRisk || (internalResetBlocked && isIdFraudRiskEnabled));
  const canShowVerifyAction =
    !!identityFraudRisk &&
    !identityFraudRisk.isError &&
    !identityFraudRisk.isManuallyVerified &&
    isIdFraudRiskEnabled &&
    ((identityFraudRisk.isNeedsReview &&
      !identityFraudRisk.isBlocked &&
      (canVerifyIdFraudRisk || internalVerifyBlocked)) ||
      (identityFraudRisk.isFailedVerifiable &&
        identityFraudRisk.providerSentilinkId != null &&
        ((canVerifyIdFraudRisk && canVerifyFailedIdFraudRisk) ||
          internalVerifyBlocked)));
  const resetDisabledReason = internalResetBlocked
    ? INTERNAL_USER_RESET_DISABLED_REASON
    : null;
  // PRODUCT-2346: keep Needs Review visible after a terminal decision, but
  // disable verification with a specific explanation instead of hiding it.
  const applicationIsApproved =
    applicationStatus === APPLICATION_STATUSES.approved;
  const dealIsCanceled = isCanceledDeal(dealStatus);
  // SENT_TO_PMS is terminal for manual verification, but intentionally stays
  // out of the shared helper because that helper also controls renter withdrawal.
  const dealIsDecisioned =
    dealStatus === DEAL_UI_STATUS_CODES.sentToEntrata ||
    isDecisionedDeal(dealStatus);
  const verifyDisabledReason = internalVerifyBlocked
    ? INTERNAL_USER_VERIFY_DISABLED_REASON
    : applicationIsApproved
    ? "Unavailable because this application is approved."
    : dealIsCanceled
    ? "Unavailable because this deal is cancelled."
    : dealIsDecisioned
    ? "Unavailable because this deal has already been decisioned."
    : null;
  const currentTierLabel = identityFraudRisk?.tierLabel ?? "Needs Review";
  const terminalTierLabel = applicationIsApproved
    ? `${currentTierLabel} at time of decision`
    : dealIsCanceled
      ? `${currentTierLabel} when deal was cancelled`
      : dealIsDecisioned
        ? `${currentTierLabel} at time of decision`
        : null;
  const verifyDisabled = isVerifyModalOpen || Boolean(verifyDisabledReason);
  const resetDisabled = isResetModalOpen || Boolean(resetDisabledReason);

  const openResetModal = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (
      !identityFraudRisk ||
      resetDisabledReason ||
      resetModalOpenRef.current
    ) {
      return;
    }
    // Flip the guard before opening so a second click in the same tick (before
    // React re-renders the disabled state) cannot stack another reset dialog.
    resetModalOpenRef.current = true;
    setIsResetModalOpen(true);
    const clear = () => {
      resetModalOpenRef.current = false;
      setIsResetModalOpen(false);
    };
    const dialog = openModalDialog(ResetIdFraudRiskModal, {
      applicationId: identityFraudRisk.applicationId,
      applicantName,
      applicantRole,
      applicantEmail: identityFraudRisk.applicantEmail,
      riskLabel: identityFraudRisk.tierLabel ?? undefined,
      // No onSuccess refetch here: `useResetIdFraudRiskCheck` optimistically flips
      // the card to "Awaiting re-submission". A refetch would be served the deal
      // summary endpoint's `Cache-Control: public, max-age=20` payload and revert
      // the card to BLOCKED for up to 20s.
    }) as { afterClose?: Promise<unknown> };
    // Re-enable the reset action once the dialog closes. If the dialog handle
    // has no afterClose promise, release the guard immediately so the action is
    // not left permanently disabled.
    if (dialog?.afterClose?.then) {
      dialog.afterClose.then(clear, clear);
    } else {
      clear();
    }
  };

  const openVerifyModal = (e?: React.MouseEvent) => {
    if (e) {
      e.stopPropagation();
    }
    if (
      !identityFraudRisk ||
      verifyDisabledReason ||
      verifyModalOpenRef.current
    ) {
      return;
    }
    verifyModalOpenRef.current = true;
    setIsVerifyModalOpen(true);
    const clear = () => {
      verifyModalOpenRef.current = false;
      setIsVerifyModalOpen(false);
    };
    const dialog = openModalDialog(VerifyIdFraudRiskModal, {
      applicationId: identityFraudRisk.applicationId,
      providerSentilinkId: identityFraudRisk.providerSentilinkId,
      applicantName,
      applicantRole,
      riskLabel: identityFraudRisk.tierLabel ?? undefined,
      reasonCount: identityFraudRisk.reasons.length,
      checkedAt: identityFraudRisk.checkedAt,
    }) as { afterClose?: Promise<unknown> };
    if (dialog?.afterClose?.then) {
      dialog.afterClose.then(clear, clear);
    } else {
      clear();
    }
  };

  const vero1Status =
    vero1Verdict?.mode === "override" && vero1Edited && vero1CardTone
      ? vero1CardTone
      : undefined;
  const vero1Adopts =
    vero1Verdict?.mode === "adopt" ||
    (vero1Verdict?.mode === "override" && !vero1Status);
  const modifiedStatus = vero1Status
    ? vero1Status
    : neutral && !vero1Adopts
      ? "pending"
      : cosignApproved
        ? "pass"
        : status;

  const handleCardKeyDown = (e: React.KeyboardEvent) => {
    if (!cardModalDetails) {
      return;
    }
    // Only handle keys aimed at the card itself. Enter/Space on nested controls
    // (the inline Reset button, the "View all details" link) bubble up here, and
    // without this guard they would open the detail modal alongside their own
    // action (PRODUCT-1346).
    if (e.target !== e.currentTarget) {
      return;
    }
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      openModal();
    }
  };

  return (
    <>
      {/*
        Rendered as a role="button" div (not a <button>) so the card can safely
        nest interactive controls — the inline Reset button, the reason-chip
        tooltip, and the "View all details" link (PRODUCT-1346).
      */}
      <div
        className={buildClassName([
          "app-summary-v2 vega flex gap small column card service",
          "status-bar",
          modifiedStatus,
        ])}
        role="button"
        tabIndex={cardModalDetails ? 0 : -1}
        onClick={cardModalDetails ? openModal : undefined}
        onKeyDown={handleCardKeyDown}
        aria-label={`View details for ${cardTitle}`}
        style={{
          cursor: cardModalDetails ? "pointer" : "default",
          background: modifiedStatus === 'fail' ? "linear-gradient(135deg, #FEF2F2 0%, #FFF 100%)" :
                     modifiedStatus === 'warn' ? "linear-gradient(135deg, #FFFBEB 0%, #FFF 100%)" :
                     modifiedStatus === 'pass' ? "linear-gradient(135deg, #F0FDF4 0%, #FFF 100%)" : "#ffffff",
          textAlign: "left",
          padding: 0,
          height: "100%",
          width: "100%",
          display: "flex",
          flexDirection: "column",
          justifyContent: "space-between",
          boxShadow: "0 1px 2px rgba(0, 0, 0, 0.04)",
          border: modifiedStatus === 'fail' ? "1px solid rgba(239, 68, 68, 0.15)" :
                  modifiedStatus === 'warn' ? "1px solid rgba(245, 158, 11, 0.15)" :
                  modifiedStatus === 'pass' ? "1px solid rgba(16, 185, 129, 0.15)" : "1px solid #e5e7eb",
          borderRadius: "8px",
          transition: "all 200ms cubic-bezier(0.4, 0, 0.2, 1)",
        }}
        onMouseEnter={(e) => {
          if (cardModalDetails) {
            e.currentTarget.style.boxShadow = "0 2px 4px rgba(0, 0, 0, 0.06)";
            e.currentTarget.style.transform = "translateY(-1px)";
          }
        }}
        onMouseLeave={(e) => {
          if (cardModalDetails) {
            e.currentTarget.style.boxShadow = "0 1px 2px rgba(0, 0, 0, 0.04)";
            e.currentTarget.style.transform = "translateY(0)";
          }
        }}
      >
        <div
          className="vega flex gap small column"
          style={{ padding: "14px", flex: "1 1 auto" }}
        >
          <div className="vega flex start center gap small">
            {lgTone ? (
              <LgStatusIcon status={modifiedStatus} />
            ) : (
              <StatusIcon status={modifiedStatus} />
            )}
            <span className="vega title">{cardTitle}</span>
          </div>
          {cosignApproved && cardTitle === "Income" &&  (
            <div className="vega flex gap small column">
              <span className="vega text" style={{ fontWeight: "bold" }}>Income guaranteed by Cosign</span>
            </div>
          )}
          {cosignApproved && cardTitle === "Credit History" &&  (
            <div className="vega flex gap small column">
              <span className="vega text" style={{ fontWeight: "bold" }}>Credit history guaranteed by Cosign</span>
            </div>
          )}
          {cosignApproved && cardTitle === "Rental History" &&  (
            <div className="vega flex gap small column">
              <span className="vega text" style={{ fontWeight: "bold" }}>Rental history guaranteed by Cosign</span>
            </div>
          )}
          {cosignApproved && cardTitle === "Criminal History" &&  (
            <div className="vega flex gap small column">
              <span className="vega text" style={{ fontWeight: "bold" }}>Criminal history guaranteed by Cosign</span>
            </div>
          )}
          {identityFraudRisk && (
            <IdentityFraudRiskBlock
              neutral={neutral}
              data={identityFraudRisk}
              canShowResetAction={canShowResetAction}
              canShowVerifyAction={canShowVerifyAction}
              atResetLimit={atResetLimit}
              resetDisabled={resetDisabled}
              resetDisabledReason={resetDisabledReason}
              verifyDisabled={verifyDisabled}
              verifyDisabledReason={verifyDisabledReason}
              terminalTierLabel={terminalTierLabel}
              onReset={openResetModal}
              onVerify={openVerifyModal}
            />
          )}
          {cardDetails.length > 0 && (
            <div className="vega flex gap small column">
              {cardDetails.map((detail) => (
                <div
                  key={
                    detail.id || `detail-label-${detail.label}-${detail.value}`
                  }
                  className="vega flex column"
                >
                  <span className="vega text">
                    {detail.label && `${detail.label}: `}
                    <span
                      style={
                        detail.status && !neutral
                          ? { color: getStatusColor(cosignApproved ? "pass" : detail.status) }
                          : undefined
                      }
                    >
                      {detail.value}
                    </span>
                  </span>
                </div>
              ))}
            </div>
          )}
          {showReviewChangesLink && (
            <Vero1UpdatedNote onReviewChanges={handleReviewChangesClick} />
          )}
        </div>

        {allDetailsLink && (
          <div
            className="app-summary-v2 card-footer"
            style={{
              flexShrink: 0,
              marginTop: "auto",
              display: "flex",
              gap: "12px",
              alignItems: "baseline",
              justifyContent: "center",
            }}
          >
            {allDetailsLink && (
              <Link
                to={allDetailsLink}
                className="app-summary-v2 vega details-link"
                onClick={handleLinkClick}
              >
                View all details
              </Link>
            )}
          </div>
        )}
      </div>

      {cardModalDetails && (
        <ApplicantDetailModal
          isOpen={isModalOpen}
          onClose={closeModal}
          cardTitle={cardTitle}
          cardDetails={cardDetails}
          cardModalDetails={cardModalDetails}
          status={modifiedStatus}
          vero1Edited={showVero1Marker}
          onReviewChanges={
            showReviewChangesLink ? handleReviewChangesClick : undefined
          }
          cosignApproved={cosignApproved}
          cosignUuid={cosignUuid}
          applicantName={applicantName}
          resetIdFraudRisk={
            canShowResetAction
              ? {
                  atResetLimit,
                  onReset: openResetModal,
                  disabled: resetDisabled,
                  disabledReason: resetDisabledReason,
                }
              : undefined
          }
          verifyIdFraudRisk={
            canShowVerifyAction
              ? {
                  onVerify: openVerifyModal,
                  disabled: verifyDisabled,
                  disabledReason: verifyDisabledReason,
                  terminalLabel:
                    terminalTierLabel ?? identityFraudRisk?.tierLabel ?? null,
                }
              : undefined
          }
          manualIdFraudRiskVerification={
            identityFraudRisk?.isManuallyVerified
              ? {
                  reviewerDisplay:
                    identityFraudRisk.manualVerification?.reviewerName ||
                    identityFraudRisk.manualVerification?.reviewerEmail ||
                    null,
                  verifiedAtDisplay: formatManualVerificationDate(
                    identityFraudRisk.manualVerification?.verifiedAt
                  ),
                  originalTierLabel: identityFraudRisk.originalTierLabel,
                }
              : null
          }
        />
      )}
    </>
  );
};

export default ApplicantGroupCard;
